import { z } from 'zod';
import {
  type ProfileSectionKind,
  profileSectionExtrasSchemaByKind,
  profileSectionItemSchema,
} from './profile-sections';

const emptyToNull = (value: unknown) => (value === '' || value === undefined ? null : value);

const formOptionalText = z
  .string()
  .optional()
  .nullable()
  .transform((value) => emptyToNull(value ?? '') as string | null);

const formOptionalUrl = z
  .union([z.literal(''), z.string().url()])
  .optional()
  .nullable()
  .transform((value) => emptyToNull(value ?? '') as string | null);

const formDateOnly = z
  .union([z.literal(''), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)])
  .optional()
  .nullable()
  .transform((value) => emptyToNull(value ?? '') as string | null);

const formCommonFields = {
  title: formOptionalText,
  subtitle: formOptionalText,
  startDate: formDateOnly,
  endDate: formDateOnly,
  location: formOptionalText,
  description: formOptionalText,
  linkUrl: formOptionalUrl,
  mediaUrl: formOptionalUrl,
};

function formExtrasSchema(kind: ProfileSectionKind): z.ZodTypeAny {
  switch (kind) {
    case 'gallery_representation':
      return z.object({
        representationType: z.string().min(1, 'Required'),
        galleryLogoUrl: formOptionalUrl,
      });
    case 'education':
      return z.object({ distinctions: formOptionalText });
    case 'tools':
      return z.object({
        category: z.string().min(1, 'Required'),
        items: z.array(z.string().min(1, 'Required')).min(1, 'Add at least one item'),
      });
    case 'studio':
      return z.object({ caption: formOptionalText });
    case 'exhibition':
      return z.object({ exhibitionType: z.enum(['solo', 'group']) });
    case 'press':
      return z.object({
        outlet: z.string().min(1, 'Required'),
        headline: z.string().min(1, 'Required'),
        author: formOptionalText,
        coverImageUrl: formOptionalUrl,
      });
    case 'career':
      return z.object({
        employer: formOptionalText,
        role: formOptionalText,
      });
    case 'other_media':
      return z.object({
        caption: formOptionalText,
        mediaType: formOptionalText,
      });
    default:
      return profileSectionExtrasSchemaByKind[kind];
  }
}

/** Client-side form schema: common fields + nested extras for one section kind. */
export function profileSectionItemFormSchema(kind: ProfileSectionKind) {
  return z
    .object({
      ...formCommonFields,
      extras: formExtrasSchema(kind),
    })
    .superRefine((data, ctx) => {
      const apiCheck = profileSectionItemSchema.safeParse({
        title: data.title,
        subtitle: data.subtitle,
        startDate: data.startDate,
        endDate: data.endDate,
        location: data.location,
        description: data.description,
        linkUrl: data.linkUrl,
        mediaUrl: data.mediaUrl,
      });
      if (!apiCheck.success) {
        for (const issue of apiCheck.error.issues) {
          ctx.addIssue({ ...issue, path: issue.path });
        }
      }
    });
}

export type ProfileSectionItemFormValues = z.infer<ReturnType<typeof profileSectionItemFormSchema>>;

export function defaultProfileSectionItemFormValues(kind: ProfileSectionKind): ProfileSectionItemFormValues {
  const base = {
    title: '',
    subtitle: '',
    startDate: '',
    endDate: '',
    location: '',
    description: '',
    linkUrl: '',
    mediaUrl: '',
  };

  switch (kind) {
    case 'gallery_representation':
      return { ...base, extras: { representationType: '', galleryLogoUrl: '' } };
    case 'education':
      return { ...base, extras: { distinctions: '' } };
    case 'tools':
      return { ...base, extras: { category: '', items: [''] } };
    case 'studio':
      return { ...base, extras: { caption: '' } };
    case 'exhibition':
      return { ...base, extras: { exhibitionType: 'solo' as const } };
    case 'press':
      return { ...base, extras: { outlet: '', headline: '', author: '', coverImageUrl: '' } };
    case 'career':
      return { ...base, extras: { employer: '', role: '' } };
    case 'other_media':
      return { ...base, extras: { caption: '', mediaType: '' } };
    default:
      return { ...base, extras: {} as ProfileSectionItemFormValues['extras'] };
  }
}

export function profileSectionItemToFormValues(
  kind: ProfileSectionKind,
  item: {
    title?: string | null;
    subtitle?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    location?: string | null;
    description?: string | null;
    linkUrl?: string | null;
    mediaUrl?: string | null;
    extras?: Record<string, unknown> | null;
  }
): ProfileSectionItemFormValues {
  const defaults = defaultProfileSectionItemFormValues(kind);
  const extras = item.extras ?? {};

  return {
    title: item.title ?? '',
    subtitle: item.subtitle ?? '',
    startDate: item.startDate ?? '',
    endDate: item.endDate ?? '',
    location: item.location ?? '',
    description: item.description ?? '',
    linkUrl: item.linkUrl ?? '',
    mediaUrl: item.mediaUrl ?? '',
    extras: {
      ...defaults.extras,
      ...extras,
      ...(kind === 'tools' && !Array.isArray(extras.items) ? { items: [''] } : {}),
    } as ProfileSectionItemFormValues['extras'],
  };
}

/** Shape sent to createSectionItem / updateSectionItem (nulls for empty optional fields). */
export function formValuesToMutationInput(kind: ProfileSectionKind, values: ProfileSectionItemFormValues) {
  const extras =
    kind === 'tools' && values.extras && 'items' in values.extras
      ? {
          ...values.extras,
          items: (values.extras.items as string[])
            .map((item: string) => item.trim())
            .filter((item: string) => item.length > 0),
        }
      : values.extras;

  return {
    title: values.title || null,
    subtitle: values.subtitle || null,
    startDate: values.startDate || null,
    endDate: values.endDate || null,
    location: values.location || null,
    description: values.description || null,
    linkUrl: values.linkUrl || null,
    mediaUrl: values.mediaUrl || null,
    extras,
  };
}
